import React, { useState, useMemo, useEffect, useRef } from 'react';

import {
  FileText,
  FileUp,
  Sparkles,
  MessageSquareText,
  FileCheck,
  Database,
  Layers,
  Search,
  CheckCircle2,
  Loader2,
  Table as TableIcon,
  BookOpen,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Zap,
  Eye,
  Code,
  Cloud,
  Trash2,
  GripVertical,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  X,
} from 'lucide-react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import { showToast } from '@/utils/toast';

import {
  askDocumentQA,
  parseSourceIntent,
  parseSourceExtract,
  commitParsedPreview,
  listDocHubDocuments,
  getDocHubDocument,
  getDocHubFileUrl,
  getDocHubMediaUrl,
  deleteDocHubDocument,
  reindexDocHubDocument,
  getDocHubIndexStatus,
  cancelDocHubDocumentReindex,
  type DocHubIndexStatus,
  listDocHubDocumentChunks,
  type DocHubChunksResult,
  type DocQAUsage,
  type ParseIntentResult,
  type ParsePreviewResult,
  type DocIntent,
  type DocQACitation,
  type DocHubDocumentSummary,
} from '../services/price-intel-service';
import { DocHubDocumentContent } from './DocHubDocumentContent';
import { DocHubChatComposer, DocHubRecallTestPanel } from './DocHubInputPanels';
import { DocHubIndexProgressModal } from './DocHubIndexProgressModal';

interface DocAssetItem {
  id: string;
  name: string;
  type: string;
  size: string;
  status: 'ready' | 'parsing' | 'error';
  updatedAt: string;
  extractedText: string;
  intentResult?: ParseIntentResult;
  fileUrl?: string;         // 原始文件 blob URL (for PDF/image/audio)
  fileBlob?: File;          // 原始 File 对象 (for docx-preview / pptx-viewer)
  docId?: string;           // 文档中枢本地库持久化 ID（md5）
  hasRawFile?: boolean;     // 本地库是否存有原始文件字节
}

interface DocChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: DocQACitation[];
  usage?: DocQAUsage;
}

const DOC_CHAT_STORAGE_PREFIX = 'price-intel:doc-chat:';
const MAX_PERSISTED_CHAT_MESSAGES = 60;

/** react-markdown 默认会清洗掉非 http(s) 协议的 URL，这里放行 media: 协议（解析产物的内联图片） */
function allowMediaUrlTransform(url: string): string {
  return url.startsWith('media:') ? url : defaultUrlTransform(url);
}

/** 后端 index 是内部段落编号；界面优先显示用户可理解的真实页码。 */
function getCitationDisplayLabel(
  citation: DocQACitation,
  fallbackIndex?: number,
): string {
  if (citation.page_number !== null && citation.page_number !== undefined) {
    return String(citation.page_number);
  }
  if (citation.index !== null && citation.index !== undefined) {
    return String(citation.index);
  }
  return String(fallbackIndex ?? '?');
}

/** 根据原文偏移量推算真实页码（取 offset 之前最后一个 page 标记）。 */
function derivePageFromOffset(rawText: string, offset: number): number | undefined {
  if (offset < 0 || !rawText) return undefined;
  const pageMarks = [...rawText.matchAll(/<!--\s*page:(\d+)\s*-->/g)].filter(
    (mark) => (mark.index ?? -1) <= offset,
  );
  return pageMarks.length > 0 ? parseInt(pageMarks[pageMarks.length - 1][1], 10) : 1;
}

/** 在原文中定位 anchor，优先在 citation 提供的 hint 附近查找，避免同名文本跳错位置。 */
function locateAnchorOffset(rawText: string, anchor: string, hintOffset?: number): number {
  if (!anchor || !rawText) return hintOffset ?? -1;
  if (typeof hintOffset === 'number' && hintOffset >= 0) {
    const windowStart = Math.max(0, hintOffset - 400);
    const windowEnd = Math.min(rawText.length, hintOffset + 12000);
    const localIndex = rawText.indexOf(anchor, windowStart);
    if (localIndex >= 0 && localIndex < windowEnd) return localIndex;
  }
  return rawText.indexOf(anchor);
}

function normalizeLookupText(value: string): string {
  return value
    .replace(/[\u200b\u200c\u200d\ufeff]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function looksLikeHeadingNeedle(value: string): boolean {
  const text = normalizeLookupText(value);
  if (!text) return true;
  if (/^\d+(\.\d+)*\s+\S+/.test(text)) return true;
  if (text.length <= 18 && !/[，。；：]/.test(text)) return true;
  return false;
}

function buildCitationLookupNeedles(citation: DocQACitation): string[] {
  const scored: Array<{ needle: string; score: number }> = [];
  const seen = new Set<string>();

  const pushNeedle = (value?: string | null, baseScore = 50) => {
    const normalized = normalizeLookupText(value || '');
    if (normalized.length < 4) return;
    const addOne = (needle: string, score: number) => {
      if (needle.length < 4 || seen.has(needle)) return;
      seen.add(needle);
      let finalScore = score;
      if (looksLikeHeadingNeedle(needle)) finalScore -= 70;
      scored.push({ needle, score: finalScore });
    };
    addOne(normalized, baseScore + Math.min(normalized.length, 40));
    // 短截断容易命中标题；仅对非标题长句保留。
    if (!looksLikeHeadingNeedle(normalized) && normalized.length > 28) {
      addOne(normalized.slice(0, 24), baseScore - 5);
    }
  };

  for (const candidate of citation.anchors || []) pushNeedle(candidate, 90);
  pushNeedle(citation.anchor, 80);
  pushNeedle(citation.row_key, 110);
  for (const line of (citation.excerpt || '').split('\n')) {
    const raw = line.trim();
    const isTableLine = raw.includes('|');
    const cleaned = raw
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/^\[(?:Section|Page)\][^\n]*/i, '')
      .replace(/[#*>`'"]/g, '')
      .replace(/^[-•+]\s+/, '')
      .trim();
    if (isTableLine) {
      const cells = cleaned
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell && !/^[-:]+$/.test(cell));
      for (const cell of [...cells].sort((a, b) => b.length - a.length)) {
        pushNeedle(cell, 120);
      }
      continue;
    }
    pushNeedle(cleaned, looksLikeHeadingNeedle(cleaned) ? 15 : 70);
  }

  scored.sort((a, b) => b.score - a.score || b.needle.length - a.needle.length);
  return scored.map((item) => item.needle);
}

function resolveCitationSearchRoot(
  docView: Element,
  targetPage: number | undefined,
  isPaginatedPresentation: boolean,
): Element {
  if (!targetPage) return docView;
  const pageAnchor = docView.querySelector(`#doc-page-${targetPage}`);
  if (!pageAnchor) return docView;
  // PPT：限制在幻灯片内；Markdown：页锚是空节点，退回父容器做 TreeWalker 兜底。
  if (isPaginatedPresentation) return pageAnchor;
  return pageAnchor.parentElement || docView;
}

function collectPageScopedElements(
  docView: Element,
  targetPage: number | undefined,
  isPaginatedPresentation: boolean,
): Element[] {
  const selector = 'p, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, pre, figcaption';
  if (!targetPage) {
    return Array.from(docView.querySelectorAll(selector));
  }
  const pageAnchor = docView.querySelector(`#doc-page-${targetPage}`);
  if (!pageAnchor) {
    return Array.from(docView.querySelectorAll(selector));
  }
  if (isPaginatedPresentation) {
    return Array.from(pageAnchor.querySelectorAll(selector));
  }

  const scoped: Element[] = [];
  let cursor: Element | null = pageAnchor.nextElementSibling;
  while (cursor) {
    if (cursor.id?.startsWith('doc-page-')) break;
    if (cursor.matches(selector)) scoped.push(cursor);
    scoped.push(...Array.from(cursor.querySelectorAll(selector)));
    cursor = cursor.nextElementSibling;
  }
  // 页内没命中时再退回全文，避免漏定位。
  return scoped.length > 0 ? scoped : Array.from(docView.querySelectorAll(selector));
}

function elementSpecificityScore(element: Element): number {
  const tag = element.tagName.toLowerCase();
  if (tag === 'td' || tag === 'th' || tag === 'tr') return 100;
  if (tag === 'li') return 80;
  if (tag === 'p' || tag === 'pre' || tag === 'blockquote') return 70;
  if (tag === 'figcaption') return 60;
  if (/^h[1-6]$/.test(tag)) return 10;
  return 40;
}

function resolveHighlightTarget(element: HTMLElement): HTMLElement {
  const isTableCell = element.tagName === 'TD' || element.tagName === 'TH';
  if (isTableCell) {
    return (element.closest('tr') || element) as HTMLElement;
  }

  // 标题命中时下沉到后续具体内容（表格/段落），避免只框住章节名。
  if (/^H[1-6]$/.test(element.tagName)) {
    let sibling: Element | null = element.nextElementSibling;
    while (sibling) {
      if (sibling.id?.startsWith('doc-page-')) break;
      if (/^H[1-6]$/.test(sibling.tagName)) break;
      const table = sibling.matches('table')
        ? sibling
        : sibling.querySelector('table');
      if (table) {
        const firstRow = table.querySelector('tbody tr, tr');
        if (firstRow) return firstRow as HTMLElement;
        return (table.closest('div') || table) as HTMLElement;
      }
      const body = sibling.matches('p, li, ul, ol, pre, blockquote')
        ? sibling
        : sibling.querySelector('p, li, pre, blockquote');
      if (body) return body as HTMLElement;
      sibling = sibling.nextElementSibling;
    }
  }
  return element;
}

function highlightCitationElement(element: HTMLElement): void {
  const target = resolveHighlightTarget(element);
  const isRow = target.tagName === 'TR' || target.tagName === 'TD' || target.tagName === 'TH';
  target.classList.add(isRow ? 'row-highlight' : 'citation-highlight');
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.style.transition = 'box-shadow 0.25s ease, background-color 0.25s ease';
  target.style.boxShadow = '0 0 0 3px rgba(0,113,227,0.45)';
  if (!isRow) {
    target.style.backgroundColor = 'rgba(0,113,227,0.10)';
  } else {
    target.style.backgroundColor = 'rgba(0,113,227,0.12)';
  }
  window.setTimeout(() => {
    target.classList.remove('citation-highlight', 'row-highlight');
    target.style.boxShadow = '';
    target.style.backgroundColor = '';
  }, 3200);
}

function findAndHighlightCitationContent(
  docView: Element,
  citation: DocQACitation,
  targetPage: number | undefined,
  isPaginatedPresentation: boolean,
): boolean {
  const needles = buildCitationLookupNeedles(citation);
  if (needles.length === 0) return false;

  const scopedElements = collectPageScopedElements(docView, targetPage, isPaginatedPresentation);
  type RankedHit = { element: HTMLElement; score: number };
  let bestHit: RankedHit | null = null;

  for (const [needleIndex, needle] of needles.entries()) {
    const normalizedNeedle = normalizeLookupText(needle);
    if (!normalizedNeedle) continue;
    const headingNeedle = looksLikeHeadingNeedle(normalizedNeedle);

    for (const element of scopedElements) {
      const elementText = normalizeLookupText(element.textContent || '');
      if (!elementText || !elementText.includes(normalizedNeedle)) continue;

      // 标题元素若几乎整段等于 needle，说明只命中了章节名，分很低。
      const tagScore = elementSpecificityScore(element);
      const almostExactHeading =
        /^h[1-6]$/i.test(element.tagName)
        && Math.abs(elementText.length - normalizedNeedle.length) <= 2;
      let score = tagScore + normalizedNeedle.length - needleIndex * 3;
      if (headingNeedle) score -= 40;
      if (almostExactHeading) score -= 50;
      // 单元格/段落包含 needle 且自身更长，通常是具体内容。
      if (tagScore >= 70 && elementText.length > normalizedNeedle.length + 4) {
        score += 25;
      }

      if (!bestHit || score > bestHit.score) {
        bestHit = { element: element as HTMLElement, score };
      }
    }
  }

  if (bestHit && bestHit.score >= 30) {
    highlightCitationElement(bestHit.element);
    return true;
  }

  // 跨文本节点兜底：优先落到非标题块。
  for (const needle of needles) {
    const searchStr = normalizeLookupText(needle).slice(0, 20);
    if (searchStr.length < 4 || looksLikeHeadingNeedle(searchStr)) continue;
    const searchRoot = resolveCitationSearchRoot(docView, targetPage, isPaginatedPresentation);
    const walker = document.createTreeWalker(searchRoot, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeText = normalizeLookupText(node.textContent || '');
      if (!nodeText.includes(searchStr)) continue;
      const block = (node.parentElement?.closest(
        'td, th, li, p, pre, blockquote, h1, h2, h3, h4, h5, h6',
      ) || node.parentElement) as HTMLElement | null;
      if (!block || !docView.contains(block)) continue;
      highlightCitationElement(block);
      return true;
    }
  }

  if (bestHit) {
    highlightCitationElement(bestHit.element);
    return true;
  }

  return false;
}

/** 将模型常用的 LaTeX 分隔符转换为 remark-math 支持的 Markdown 数学语法。 */
function normalizeMarkdownMathDelimiters(markdown: string): string {
  return markdown
    .split(/(```[\s\S]*?```)/g)
    .map((segment) => {
      if (segment.startsWith('```')) return segment;
      return segment
        .replace(/\\\[([\s\S]*?)\\\]/g, (_match, formula: string) => {
          return `\n\n$$\n${formula.trim()}\n$$\n\n`;
        })
        .replace(/\\\(([\s\S]*?)\\\)/g, (_match, formula: string) => {
          return `$${formula.trim()}$`;
        });
    })
    .join('');
}

/** 模型在中文语境常输出 **"引用语"** 这类写法；按 CommonMark 定界符规则，
 * ** 紧跟引号且前邻 CJK 字符时不构成强调，星号会被原样显示。
 * 把引号（含中文书名号/括号）移到强调标记外侧，保证加粗正常渲染。 */
function normalizeEmphasisAroundQuotes(markdown: string): string {
  return markdown
    .split(/(```[\s\S]*?```)/g)
    .map((segment) =>
      segment.startsWith('```')
        ? segment
        : segment.replace(
            /\*\*(["“‘'（【《])([^\n*]{1,300}?)(["”’'）】》])\*\*/g,
            '$1**$2**$3',
          ),
    )
    .join('');
}

/** 把图片证据直接注入图片引用首次出现的位置，避免复杂 Markdown 嵌套导致运行时扫描漏图。 */
function prepareAssistantMarkdown(
  markdown: string,
  citations: DocQACitation[] | undefined,
): string {
  let preparedMarkdown = normalizeEmphasisAroundQuotes(normalizeMarkdownMathDelimiters(markdown));
  if (!citations?.length) return preparedMarkdown;

  preparedMarkdown = preparedMarkdown.replace(/\[(\d+)\]/g, (originalMarker, indexText: string) => {
    const citationIndex = Number(indexText);
    return citations.some((citation) => citation.index === citationIndex)
      ? `⟦${citationIndex}⟧`
      : originalMarker;
  });

  const injectedImageKeys = new Set<string>();
  for (const citation of citations) {
    if (citation.type !== 'image' || !citation.media_filename || citation.index === undefined) continue;
    const imageKey = citation.image_id || citation.media_filename;
    if (injectedImageKeys.has(imageKey)) continue;

    const citationMarker = `⟦${citation.index}⟧`;
    if (!preparedMarkdown.includes(citationMarker)) continue;

    const safeCaption = (citation.caption || `第 ${citation.page_number || '?'} 页配图`)
      .replace(/[\[\]\r\n]+/g, ' ')
      .trim()
      .slice(0, 160);
    preparedMarkdown = preparedMarkdown.replace(
      citationMarker,
      `${citationMarker}\n\n![${safeCaption}](media:${citation.media_filename})\n\n`,
    );
    injectedImageKeys.add(imageKey);
  }

  return preparedMarkdown;
}

/** 后端文档摘要 → 前端文档资产（全文懒加载，选中时再拉详情） */
function summaryToDocAsset(summary: DocHubDocumentSummary): DocAssetItem {
  const ext = (summary.file_ext || '').toLowerCase();
  const type =
    ext === 'pdf' ? 'pdf'
      : ['xlsx', 'xls', 'csv'].includes(ext) ? 'excel'
        : ['pptx', 'ppt', 'odp'].includes(ext) ? 'ppt'
          : ['png', 'jpg', 'jpeg', 'webp'].includes(ext) ? 'image'
            : ['mp3', 'wav', 'm4a', 'aac'].includes(ext) ? 'audio'
              : 'word';
  return {
    id: summary.doc_id,
    docId: summary.doc_id,
    name: summary.filename,
    type,
    size: summary.file_size_bytes ? `${(summary.file_size_bytes / 1024).toFixed(1)} KB` : '—',
    status: 'ready',
    updatedAt: (summary.updated_at || '').slice(0, 16).replace('T', ' '),
    extractedText: '',
    hasRawFile: summary.has_raw_file,
    intentResult: {
      sourceName: summary.filename,
      sourceKind: summary.source_kind,
      mimeType: summary.mime_type,
      pipeline: summary.pipeline || [],
      contentType: summary.content_type || 'normalized_text',
      fingerprint: summary.fingerprint,
      intent: summary.intent,
      extractedTextExcerpt: '',
      extractedTextLength: summary.extracted_text_length,
      docId: summary.doc_id,
      aiSummary: summary.ai_summary || undefined,
    },
  };
}

const INITIAL_DOCS: DocAssetItem[] = [
  {
    id: 'doc-0',
    name: '报价单_国内_标准版_2026-06-25 (2).xlsx',
    type: 'excel',
    size: '14.3 KB',
    status: 'ready',
    updatedAt: '刚刚',
    extractedText: `# sheet: 报价单\n| 项目 | 字段/内容 | 明细 1 | 明细 2 | 详细参数 | 价格/单位 |\n| --- | --- | --- | --- | --- | --- |\n| 客户名称 | 石家庄市晨日电子科技有限公司 | 报价单号 | QT-20260624-021 | 报价日期 | 2026-06-24 |\n| 销售代表 | 乔川川 | 适用场景 | 1 新场景-1 / 1.1 大屏-1 | 屏幕数量 | 2 块 |\n| 参数名称 | 宽度 | 高度 | 单位 | 连体排列 | 4 x 4 列 x 行 |\n| 屏幕尺寸 | 2.40m | 1.35m | m | 分辨率 | 1920 x 1080 px |\n| 总重量 | 64 kg | 最大功耗 | 912 W | 典型功耗 | 384.00 W |\n| 对角线尺寸| 108.4 英寸 | 显示面积 | 3.24 m² | 宽高比 | 1.78 |\n| 箱体数量 | 16 个 | - | - | - | - |\n\n## 1、主体及模组清单\n| 序号 | 产品名称 | 型号/规格 | 数量 | 单位 | 单价 (CNY) | 总价 (CNY) |\n| --- | --- | --- | --- | --- | --- | --- |\n| 1 | UMini1.5-1.25COB | UMini1.5-1.25COB (800nits) | 3.24 | sqt | 11449.36 | 37095.94 |\n\n## 2、配件与控制系统明细\n| 序号 | 产品名称 | 型号/规格 | 数量 | 单位 | 单价 (CNY) | 总价 (CNY) |\n| --- | --- | --- | --- | --- | --- | --- |\n| 1 | 线材包 | ULWIII/ULWIII PRO/ULWIII GS/UMiniW | 1 | 套 | 包含 | 包含 |\n| 2 | 主控视频处理器 | Novastar VX1000 4K 独立主控 | 1 | 台 | 3200.00 | 3200.00 |\n| 3 | 接收卡 | Armor A10s Plus 扩展接收卡 | 16 | 张 | 包含 | 包含 |\n| 4 | 电源模块 | 5V 60A 沉金低功耗电源 | 8 | 组 | 包含 | 包含 |\n| 5 | 前维护磁吸工具 | 智能气动吸提器组 | 1 | 套 | 包含 | 包含 |\n| 6 | 运输包装 | 航空防震木箱包装 | 2 | 箱 | 包含 | 包含 |\n\n<!-- page:1 -->\n## 3、商务与付款条款\n| 条款项目 | 具体约定内容 |\n| --- | --- |\n| 报价有效期 | 2026-06-25 至 2026-07-25 |\n| 付款方式 | 30% 预付款，60% 发货前付清，10% 验收后付清 |\n| 质保承诺 | 包含 5 年原厂质保与终身技术协同支持 |`,
    intentResult: {
      sourceName: '报价单_国内_标准版_2026-06-25 (2).xlsx',
      sourceKind: 'file',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      pipeline: ['upload', 'pandas_excel', 'markdown_table'],
      contentType: 'markdown_table',
      fingerprint: {
        char_count: 3956,
        table_count: 4,
        paragraph_count: 6,
        price_signal_count: 8,
        spec_signal_count: 6,
        competitor_mentions: ['Unilumin'],
        region_mentions: ['Middle East', 'China'],
        quarter_mentions: ['2026Q2'],
        has_price_table: true,
        dominant_block_type: 'markdown_table',
      },
      intent: {
        intent: 'price_quote',
        confidence: 0.96,
        intent_source: 'rule',
        reason: '检测到 8 处价格信号及 4 张包含明细与商务条款的标准数据表',
        suggested_fields: ['competitor', 'model', 'pitch_mm', 'price_per_sqm', 'currency', 'region'],
        alternatives: ['spec_sheet', 'research_note'],
        needs_user_confirm: false,
      },
      aiSummary: {
        headline: '本文件为【洲明 (Unilumin) UMini1.5-1.25COB 官方项目报价单】，面向石家庄市晨日电子科技有限公司，包含主体模组、Novastar VX1000 处理器及商务付款条款。',
        key_points: [
          '项目客户：石家庄市晨日电子科技有限公司（乔川川代表）',
          '核心规格：UMini1.5-1.25COB 800nits，屏幕尺寸 2.40m x 1.35m (108.4英寸)',
          '价格明细：模组总计 ¥37,095.94，系统及主控整套方案落地，质保 5 年',
        ],
        suggested_followups: [
          '石家庄晨日电子的报价明细与整套总价？',
          'UMini1.5-1.25COB 的显示面积与最大功耗？',
          '详细的商务付款比例与 5 年质保条款？',
        ],
        summary_source: 'llm',
      },
      extractedTextExcerpt: '全量 3956 字符，包含全表 30+ 行明细',
      extractedTextLength: 3956,
    },
  },
  {
    id: 'doc-1',
    name: 'elsa0708_中东报价单.xlsx',
    type: 'excel',
    size: '48.2 KB',
    status: 'ready',
    updatedAt: '刚刚',
    extractedText: `# sheet: Quotation\n| Competitor | Series | Model | Pitch (mm) | Price / sqm | Region |\n| --- | --- | --- | --- | --- | --- |\n| Absen | BNXII | BNXII1.5 | 1.56 | USD 1553.595 | Middle East |\n| Unilumin | UMini W | UMini W1.5 | 1.56 | USD 1780.490 | Middle East |\n\n<!-- page:1 -->\n备注：包含 2 处价格点，Absen 的交付周期为 3 周，中东完税价格。`,
    intentResult: {
      sourceName: 'elsa0708_中东报价单.xlsx',
      sourceKind: 'file',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      pipeline: ['upload', 'pandas_excel', 'markdown_table'],
      contentType: 'markdown_table',
      fingerprint: {
        char_count: 480,
        table_count: 1,
        paragraph_count: 2,
        price_signal_count: 4,
        spec_signal_count: 2,
        competitor_mentions: ['Absen', 'Unilumin'],
        region_mentions: ['Middle East'],
        quarter_mentions: ['2026Q2'],
        has_price_table: true,
        dominant_block_type: 'markdown_table',
      },
      intent: {
        intent: 'price_quote',
        confidence: 0.92,
        intent_source: 'rule',
        reason: '检测到 4 处价格信号及标准价格表格结构',
        suggested_fields: ['competitor', 'model', 'pitch_mm', 'price_per_sqm', 'currency', 'region'],
        alternatives: ['spec_sheet', 'research_note'],
        needs_user_confirm: false,
      },
      aiSummary: {
        headline: '本文件为【中东大区竞品报价单】，包含 Absen BNXII1.5 与 Unilumin UMini W1.5 针对 1.56mm 小间距场景的 CIF 完税报价对比。',
        key_points: [
          'Absen BNXII1.5 单价: USD 1553.595/m² (交期 3 周)',
          'Unilumin UMini W1.5 单价: USD 1780.490/m²',
          '覆盖市场: Middle East (中东地区)',
        ],
        suggested_followups: [
          'Absen BNXII1.5 的中东单价与交付周期？',
          'Unilumin UMini W1.5 的平米价格对比？',
          '这份报价单包含哪些关税与运输条件？',
        ],
        summary_source: 'llm',
      },

      extractedTextExcerpt: 'Absen BNXII1.5 / Unilumin UMini W1.5 500x1000mm 1.56mm USD 1553.595 Middle East',
      extractedTextLength: 480,
    },
  },

  {
    id: 'doc-2',
    name: '南欧竞品调研纪要_2026Q1.docx',
    type: 'word',
    size: '124.5 KB',
    status: 'ready',
    updatedAt: '20 分钟前',
    extractedText: `# 南欧 LED 显示屏竞品考察纪要\n\n## 一、考察概述\n本次考察覆盖西班牙、葡萄牙和意大利三个主要大区。重点关注 Leyard 和 Absen 在小间距市场的布局。\n\n## 二、关键价格线索\n<!-- page:1 -->\nLeyard 在西班牙大区针对 P1.2 项目的报价约为 €2100/平米；Absen 报价处于 €1950/平米。\n另外发现洲明 (Unilumin) 在南欧推出了新的户外固装系列，目前暂无公开报价，但提供 5 年质保。`,
    intentResult: {
      sourceName: '南欧竞品调研纪要_2026Q1.docx',
      sourceKind: 'file',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      pipeline: ['upload', 'anydoc', 'markdown'],
      contentType: 'markdown',
      fingerprint: {
        char_count: 620,
        table_count: 0,
        paragraph_count: 4,
        price_signal_count: 2,
        spec_signal_count: 1,
        competitor_mentions: ['Leyard', 'Absen', 'Unilumin'],
        region_mentions: ['Spain', 'Portugal', 'Italy', 'Southern Europe'],
        quarter_mentions: ['2026Q1'],
        has_price_table: false,
        dominant_block_type: 'paragraph',
      },
      intent: {
        intent: 'research_note',
        confidence: 0.82,
        intent_source: 'rule',
        reason: '包含富集地区线索与零散竞品价格，判定为市场调研纪要',
        suggested_fields: ['competitor', 'region', 'price_trend', 'key_findings'],
        alternatives: ['price_quote', 'general'],
        needs_user_confirm: true,
      },
      extractedTextExcerpt: '本次考察覆盖西班牙、葡萄牙和意大利三个主要大区。重点关注 Leyard 和 Absen...',
      extractedTextLength: 620,
    },
  },
];

export function DocHubStudioPage() {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const hydratedConversationKeyRef = useRef<string | null>(null);
  const [docs, setDocs] = useState<DocAssetItem[]>(INITIAL_DOCS);
  const [selectedDocId, setSelectedDocId] = useState<string>('doc-1');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'rendered' | 'original' | 'source'>('rendered');


  const [activeRightTab, setActiveRightTab] = useState<'chat' | 'extract' | 'kb'>('chat');
  const [leftPanelWidth, setLeftPanelWidth] = useState(290);
  const [rightPanelWidth, setRightPanelWidth] = useState(380);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);

  // 图片点击放大预览（灯箱）
  const [imagePreview, setImagePreview] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!imagePreview) return;
    const handleEscapeKey = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') setImagePreview(null);
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [imagePreview]);

  // AI 问答状态
  const [messages, setMessages] = useState<DocChatMessage[]>([
    {
      role: 'assistant',
      content: '欢迎使用 Universal Doc Hub 智能平台！我已经对该文档完成了无损版面解析与知识库切片。您可以向我询问文档中的任何具体参数、价格或条款。',
    },
  ]);
  const [chatLoading, setChatLoading] = useState(false);

  // 专属提问推荐：动态推荐（来自每轮问答响应）+ 折叠控制（null 表示自动：未提问时展开，提问后折叠）
  const [dynamicFollowups, setDynamicFollowups] = useState<string[] | null>(null);
  const [followupPanelOverride, setFollowupPanelOverride] = useState<boolean | null>(null);

  // 动态抽取状态
  const [currentIntent, setCurrentIntent] = useState<DocIntent>('price_quote');
  const [extracting, setExtracting] = useState(false);
  const [extractedPreview, setExtractedPreview] = useState<ParsePreviewResult | null>(null);

  // 向量检索索引重建：弹窗进度 + 可取消
  const [indexTrackingDocId, setIndexTrackingDocId] = useState<string | null>(null);
  const [indexModalVisible, setIndexModalVisible] = useState(false);
  const [indexStatus, setIndexStatus] = useState<DocHubIndexStatus | null>(null);
  const [indexCancelling, setIndexCancelling] = useState(false);
  const indexPollDocIdRef = useRef<string | null>(null);
  const indexTerminalNotifiedRef = useRef<string | null>(null);

  // 知识库面板：切片数据 + 召回测试
  const [kbChunks, setKbChunks] = useState<DocHubChunksResult | null>(null);
  const [kbChunksLoading, setKbChunksLoading] = useState(false);
  const [kbChunksError, setKbChunksError] = useState<string | null>(null);
  const selectedDoc = useMemo(
    () => docs.find((d) => d.id === selectedDocId) || docs[0],
    [docs, selectedDocId]
  );

  const activeConversationStorageKey = useMemo(() => {
    if (!selectedDoc) return null;
    return `${DOC_CHAT_STORAGE_PREFIX}${selectedDoc.docId || selectedDoc.id}`;
  }, [selectedDoc]);

  const filteredDocs = useMemo(
    () => docs.filter((d) => d.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [docs, searchQuery]
  );

  const fp = selectedDoc?.intentResult?.fingerprint;
  const indexTrackingDoc = useMemo(
    () => docs.find((doc) => doc.docId === indexTrackingDocId) || null,
    [docs, indexTrackingDocId],
  );

  // 启动时从本地 Doc Hub 库拉取已持久化的文档（验证阶段：本地 Docker PG 为真实数据源）
  // 后端不可用或库为空时，保留内置演示文档
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const summaries = await listDocHubDocuments();
        if (cancelled || summaries.length === 0) return;
        const mapped = summaries.map(summaryToDocAsset);
        setDocs(mapped);
        setSelectedDocId((prev) => (mapped.some((d) => d.id === prev) ? prev : mapped[0].id));
      } catch {
        // 后端不可用：保留演示数据
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 选中文档后懒加载全文详情 + 原始文件（列表接口不含全文）
  useEffect(() => {
    const doc = docs.find((d) => d.id === selectedDocId);
    if (!doc || !doc.docId || doc.extractedText) return;
    const targetDocId = doc.docId;
    const targetId = doc.id;
    let cancelled = false;
    (async () => {
      try {
        const detail = await getDocHubDocument(targetDocId);
        if (cancelled) return;
        const fileUrl = detail.has_raw_file ? getDocHubFileUrl(targetDocId) : undefined;
        let fileBlob: File | undefined;
        const officeExts = ['pptx', 'ppt', 'docx', 'doc'];
        if (fileUrl && officeExts.includes((detail.file_ext || '').toLowerCase())) {
          try {
            const resp = await fetch(fileUrl);
            const blob = await resp.blob();
            fileBlob = new File([blob], detail.filename, { type: detail.mime_type || blob.type });
          } catch {
            // 原件拉取失败仍可查看解析文本
          }
        }
        if (cancelled) return;
        setDocs((prev) =>
          prev.map((d) =>
            d.id === targetId ? { ...d, extractedText: detail.extracted_text || '', fileUrl, fileBlob } : d,
          ),
        );
      } catch {
        // 详情拉取失败：保持摘要态
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDocId, docs]);

  // 删除文档（本地库记录 + 磁盘原件 + 向量切片）
  const handleDeleteDoc = async (doc: DocAssetItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!doc.docId) return;
    try {
      await deleteDocHubDocument(doc.docId);
      try {
        localStorage.removeItem(`${DOC_CHAT_STORAGE_PREFIX}${doc.docId}`);
      } catch {
        // 文档删除不应被浏览器存储异常阻断。
      }
      setDocs((prev) => {
        const next = prev.filter((d) => d.id !== doc.id);
        if (selectedDocId === doc.id && next.length > 0) {
          setSelectedDocId(next[0].id);
        }
        return next;
      });
      showToast(`已删除 《${doc.name}》`);
    } catch (err) {
      showToast(`删除失败: ${(err as Error).message}`);
    }
  };

  const reindexing = Boolean(
    indexTrackingDocId
    && selectedDoc?.docId === indexTrackingDocId
    && (indexStatus?.index_status === 'queued' || indexStatus?.index_status === 'running'),
  );

  const refreshIndexStatus = async (docId: string) => {
    const result = await getDocHubIndexStatus(docId);
    setIndexStatus(result);
    return result;
  };

  const handleReindexDoc = async () => {
    if (!selectedDoc?.docId || reindexing) return;
    const targetDocId = selectedDoc.docId;
    setIndexTrackingDocId(targetDocId);
    setIndexModalVisible(true);
    setIndexCancelling(false);
    indexTerminalNotifiedRef.current = null;
    try {
      const result = await reindexDocHubDocument(targetDocId);
      await refreshIndexStatus(targetDocId);
      const strategyLabel = result.index_strategy === 'on_demand'
        ? 'span 向量索引'
        : result.index_strategy === 'tabular_search'
          ? '表格行检索地图'
          : result.index_strategy === 'direct'
            ? '全文直送结构'
            : '混合检索索引';
      if (result.resumed && result.checkpoint_embedded_count) {
        const totalPart = result.checkpoint_embed_total
          ? `/${result.checkpoint_embed_total}`
          : '';
        showToast(`将从 ${result.checkpoint_embedded_count}${totalPart} 继续重建${strategyLabel}`);
      } else {
        showToast(`已开始重建${strategyLabel}${result.uses_media_enriched_text ? '（含图片理解内容）' : ''}`);
      }
    } catch (err) {
      setIndexModalVisible(false);
      setIndexTrackingDocId(null);
      setIndexStatus(null);
      showToast(`重建索引失败: ${(err as Error).message}`);
    }
  };

  const handleCancelIndexRebuild = async () => {
    if (!indexTrackingDocId || indexCancelling) return;
    setIndexCancelling(true);
    try {
      // 第二次点击「取消中」时强制结束，避免服务重启后僵尸任务一直卡在取消中。
      const force = Boolean(indexStatus?.index_cancel_requested);
      const result = await cancelDocHubDocumentReindex(indexTrackingDocId, { force });
      showToast(result.message);
      await refreshIndexStatus(indexTrackingDocId);
    } catch (err) {
      showToast(`取消失败: ${(err as Error).message}`);
    } finally {
      setIndexCancelling(false);
    }
  };

  const handleCloseIndexModal = () => {
    setIndexModalVisible(false);
    if (
      indexStatus
      && (indexStatus.index_status === 'ready'
        || indexStatus.index_status === 'failed'
        || indexStatus.index_status === 'cancelled')
    ) {
      setIndexTrackingDocId(null);
      setIndexStatus(null);
      setIndexCancelling(false);
    }
  };

  // 知识库面板：加载切片数据（切换文档时重置）
  const loadKbChunks = async (docId: string) => {
    setKbChunksLoading(true);
    setKbChunksError(null);
    try {
      const result = await listDocHubDocumentChunks(docId);
      setKbChunks(result);
    } catch (err) {
      setKbChunksError((err as Error).message);
      setKbChunks(null);
    } finally {
      setKbChunksLoading(false);
    }
  };

  useEffect(() => {
    setKbChunks(null);
    setKbChunksError(null);
    if (activeRightTab === 'kb' && selectedDoc?.docId) {
      void loadKbChunks(selectedDoc.docId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc?.docId, activeRightTab]);

  useEffect(() => {
    indexPollDocIdRef.current = indexTrackingDocId;
  }, [indexTrackingDocId]);

  useEffect(() => {
    const docId = indexTrackingDocId;
    const isActive = indexStatus?.index_status === 'queued' || indexStatus?.index_status === 'running';
    if (!docId || !isActive) return undefined;

    const pollTimer = window.setInterval(() => {
      void getDocHubIndexStatus(docId)
        .then((result) => {
          if (indexPollDocIdRef.current !== docId) return;
          setIndexStatus(result);
        })
        .catch(() => {
          // 轮询失败时不打断现有进度展示。
        });
    }, 1500);

    return () => window.clearInterval(pollTimer);
  }, [indexTrackingDocId, indexStatus?.index_status]);

  useEffect(() => {
    if (!indexTrackingDocId || !indexStatus) return;
    const terminalStatuses = new Set(['ready', 'failed', 'cancelled']);
    if (!terminalStatuses.has(indexStatus.index_status)) return;
    const notifyKey = `${indexTrackingDocId}:${indexStatus.index_status}:${indexStatus.index_error || ''}`;
    if (indexTerminalNotifiedRef.current === notifyKey) return;
    indexTerminalNotifiedRef.current = notifyKey;
    void loadKbChunks(indexTrackingDocId);
    if (indexStatus.index_status === 'ready') {
      showToast(`索引重建完成：${indexStatus.vector_chunk_count} 个向量切片`);
    } else if (indexStatus.index_status === 'cancelled') {
      const saved = indexStatus.checkpoint_embedded_count || indexStatus.index_embedded_count;
      const total = indexStatus.checkpoint_embed_total || indexStatus.index_embed_total;
      showToast(
        saved > 0
          ? `索引重建已取消，已保存 ${saved}${total ? `/${total}` : ''}，下次将自动继续`
          : '索引重建已取消',
      );
    } else if (indexStatus.index_error) {
      showToast(`索引重建失败: ${indexStatus.index_error}`);
    }
  }, [indexStatus, indexTrackingDocId]);

  useEffect(() => {
    const docId = selectedDoc?.docId;
    if (!docId) return;
    let cancelled = false;
    void getDocHubIndexStatus(docId)
      .then((result) => {
        if (cancelled) return;
        if (result.index_status === 'queued' || result.index_status === 'running') {
          setIndexTrackingDocId(docId);
          setIndexStatus(result);
          setIndexModalVisible(true);
        }
      })
      .catch(() => {
        // 忽略读取失败，不影响页面其它功能。
      });
    return () => { cancelled = true; };
  }, [selectedDoc?.docId]);

  // 文档问答路径分级：优先用后端 chunks 接口返回的 qa_tier（与后端阈值同源），
  // 未加载时按本地阈值估算兜底。
  const qaTierInfo = useMemo(() => {
    if (!selectedDoc) return null;
    const backendTier = kbChunks?.qa_tier;
    const directLimit = kbChunks?.qa_tier_thresholds?.direct_limit ?? 30000;
    const onDemandLimit = kbChunks?.qa_tier_thresholds?.on_demand_limit ?? 400000;

    const tierKey = backendTier ?? (() => {
      if (selectedDoc.type === 'excel') return 'tabular';
      const textLength = selectedDoc.intentResult?.extractedTextLength ?? 0;
      if (textLength < directLimit) return 'direct';
      if (textLength < onDemandLimit) return 'hybrid';
      return 'on_demand';
    })();

    const tierMap: Record<string, { tier: string; color: string; description: string; reindexLabel: string }> = {
      tabular: {
        tier: '表格通道',
        color: 'text-violet-600 bg-violet-500/10 border-violet-500/30',
        description: '行级精确搜索：按问题关键词过滤匹配行，表头+命中行直送 LLM',
        reindexLabel: '重建表格检索索引',
      },
      direct: {
        tier: '全文直送',
        color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/30',
        description: `短文档（< ${(directLimit / 1000).toFixed(0)}K 字符）：全文直接进入 LLM 上下文，零信息损耗，不走向量检索`,
        reindexLabel: '重建检索结构',
      },
      hybrid: {
        tier: '混合检索',
        color: 'text-blue-600 bg-blue-500/10 border-blue-500/30',
        description: `中等文档（${(directLimit / 1000).toFixed(0)}K ~ ${(onDemandLimit / 1000).toFixed(0)}K 字符）：普通向量+全文检索，不生成逐块 LLM 上下文和 RAPTOR`,
        reindexLabel: '重建混合检索索引',
      },
      on_demand: {
        tier: '按需向量检索',
        color: 'text-amber-600 bg-amber-500/10 border-amber-500/30',
        description: `超大文档（> ${(onDemandLimit / 1000).toFixed(0)}K 字符）：按章节 span 建向量索引，语义召回 + Rerank，无需维护领域关键词表`,
        reindexLabel: '重建 span 向量索引',
      },
    };
    return { key: tierKey, ...(tierMap[tierKey] ?? tierMap.direct) };
  }, [selectedDoc, kbChunks]);

  // 1. AI 智能概要总结 (Executive Summary)
  const docSummary = useMemo(() => {
    if (!selectedDoc) return null;

    const compStr = fp?.competitor_mentions?.length ? fp.competitor_mentions.join(' / ') : '品牌竞品';
    const regStr = fp?.region_mentions?.length ? fp.region_mentions.join(' / ') : '目标区域';

    // 如果后端大模型返回了深层 AI 总结，直接优先使用真实 LLM 的总结与快捷提问！
    if (selectedDoc.intentResult?.aiSummary) {
      return {
        headline: selectedDoc.intentResult.aiSummary.headline,
        keyPoints: selectedDoc.intentResult.aiSummary.key_points || [],
        suggestedFollowups: selectedDoc.intentResult.aiSummary.suggested_followups || [
          `${compStr} 的核心报价明细与单价是多少？`,
          `${regStr} 区域的商务与付款条款约定？`,
          '提取文档中的关键规格参数与价格列',
        ],
      };
    }

    const intentName = selectedDoc.intentResult?.intent.intent || 'price_quote';

    if (intentName === 'price_quote') {
      return {
        headline: `本文件为【竞品报价清单】，重点汇总了 ${compStr} 针对 ${regStr} 地区的最新产品单价与交期说明。`,
        keyPoints: [
          `核心品牌：${compStr}`,
          `覆盖区域：${regStr}`,
          `数据密度：包含 ${fp?.price_signal_count ?? 0} 处价格线索及结构化数据表`,
        ],
        suggestedFollowups: [
          `${compStr} 的具体型号与价格明细？`,
          `${regStr} 市场的交付周期与质保条款？`,
          `提取该报价单包含的所有列与单价数据`,
        ],
      };
    } else if (intentName === 'research_note') {
      return {
        headline: `本文件为【市场调研考察纪要】，记录了 ${regStr} 地区关于 ${compStr} 的最新市场动作与竞品线索。`,
        keyPoints: [
          `考察区域：${regStr}`,
          `重点竞品：${compStr}`,
          `核心情报：包含大区项目报价区间与代理商务条款`,
        ],
        suggestedFollowups: [
          `${compStr} 在 ${regStr} 的最新价格动向？`,
          `考察报告得出的核心市场结论有哪些？`,
          `涉及哪些具体的代表性项目与型号？`,
        ],
      };
    } else {
      return {
        headline: `本文件为【通用商务技术文档】，系统已完成全量结构化解析与语义切片。`,
        keyPoints: [
          `文本字符：${fp?.char_count ?? 0} 字`,
          `包含表格：${fp?.table_count ?? 0} 张`,
          `关键实体：${compStr} / ${regStr}`,
        ],
        suggestedFollowups: [
          `总结该文档的核心要点与主题`,
          `提取文档中的具体数值与表格数据`,
          `查询文档中的条款或注意事项`,
        ],
      };
    }
  }, [selectedDoc, fp]);

  // 专属提问推荐：已问过的问题（含手动输入）不再出现在推荐中；
  // 未提问时默认展开，提问过后默认折叠，用户可手动切换。
  const askedQuestionTexts = useMemo(
    () =>
      messages
        .filter((message) => message.role === 'user')
        .map((message) => message.content.replace(/\s+/g, ' ').trim()),
    [messages],
  );
  const followupsExpanded = followupPanelOverride ?? (askedQuestionTexts.length === 0);
  const followupChips = useMemo(() => {
    const source = dynamicFollowups
      ?? docSummary?.suggestedFollowups
      ?? ['提取核心报价明细与单价？', '主要商务与履约条款？', '关键规格参数表有哪些？'];
    const askedSet = new Set(askedQuestionTexts);
    return source.filter((chip) => !askedSet.has(chip.replace(/\s+/g, ' ').trim()));
  }, [dynamicFollowups, docSummary, askedQuestionTexts]);

  // 对话按文档保存在当前浏览器中，页面刷新或切换文档后可恢复各自的问答上下文。
  useEffect(() => {
    if (
      !activeConversationStorageKey
      || hydratedConversationKeyRef.current !== activeConversationStorageKey
    ) {
      return;
    }
    try {
      localStorage.setItem(
        activeConversationStorageKey,
        JSON.stringify(messages.slice(-MAX_PERSISTED_CHAT_MESSAGES)),
      );
    } catch {
      // 浏览器禁用本地存储或空间不足时，不影响当前会话继续使用。
    }
  }, [activeConversationStorageKey, messages]);

  // 切换文档时重置专属提问推荐的动态结果与折叠状态
  useEffect(() => {
    setDynamicFollowups(null);
    setFollowupPanelOverride(null);
  }, [selectedDocId]);

  useEffect(() => {
    if (!selectedDoc || !activeConversationStorageKey) return;

    let restoredMessages: DocChatMessage[] | null = null;
    try {
      const storedValue = localStorage.getItem(activeConversationStorageKey);
      if (storedValue) {
        const parsedValue: unknown = JSON.parse(storedValue);
        if (
          Array.isArray(parsedValue)
          && parsedValue.every(
            (message) =>
              message
              && typeof message === 'object'
              && ('role' in message)
              && (message.role === 'user' || message.role === 'assistant')
              && ('content' in message)
              && typeof message.content === 'string',
          )
        ) {
          restoredMessages = parsedValue as DocChatMessage[];
        }
      }
    } catch {
      restoredMessages = null;
    }

    const headlinePart = docSummary?.headline
      ? `\n\n💡 **AI 提炼文档概览**：${docSummary.headline}`
      : '';
    const welcomeMessage: DocChatMessage = {
      role: 'assistant',
      content: `您好！我已经对文档 **《${selectedDoc.name}》** 完成了全量版面解析与知识库切片。${headlinePart}\n\n您可以向我询问文档中的任何具体参数、价格或条款，或者直接点击下方针对该文档提炼的专属快捷问题：`,
    };

    setMessages(restoredMessages?.length ? restoredMessages : [welcomeMessage]);
    hydratedConversationKeyRef.current = activeConversationStorageKey;
  }, [activeConversationStorageKey]);

  // 页码跳转功能
  const handleJumpToPage = (pageNum: number) => {
    if (viewMode !== 'rendered') {
      setViewMode('rendered');
    }
    setTimeout(() => {
      const el = document.getElementById(`doc-page-${pageNum}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // 增加高亮动效
        const marker = (el.matches('[data-presentation-slide]') ? el : el.nextElementSibling) as HTMLElement;
        if (marker) {
          marker.style.transition = 'all 0.3s ease-in-out';
          marker.style.transform = 'scale(1.02)';
          marker.style.boxShadow = '0 0 0 2px var(--color-key)';
          setTimeout(() => {
            marker.style.transform = 'none';
            marker.style.boxShadow = 'none';
          }, 1500);
        }
      }
    }, 150); // 给渲染引擎一点切换时间
  };

  /**
   * 引用定位：PPT 必须以切分后的 page_number 为唯一页码来源。
   * text_offset 基于后端原始文本，媒体理解写回 Markdown 后偏移会变化，不能再用于覆盖 PPT 页码。
   */
  const jumpToCitation = (citation: DocQACitation) => {
    if (viewMode !== 'rendered') {
      setViewMode('rendered');
    }
    setTimeout(() => {
      const rawText = selectedDoc?.extractedText || '';
      const needles = buildCitationLookupNeedles(citation);
      const primaryAnchor = needles[0] || '';
      const isPaginatedPresentation = selectedDoc?.type === 'ppt';

      const hintOffset =
        typeof citation.text_offset === 'number' && citation.text_offset >= 0
          ? citation.text_offset
          : undefined;
      let bestAnchorOffset = -1;
      for (const needle of needles) {
        const offset = locateAnchorOffset(rawText, needle, hintOffset);
        if (offset >= 0) {
          bestAnchorOffset = offset;
          break;
        }
      }
      const offsetForPage =
        !isPaginatedPresentation && bestAnchorOffset >= 0
          ? bestAnchorOffset
          : hintOffset ?? -1;

      let targetPage = citation.page_number ?? undefined;
      if (!isPaginatedPresentation && offsetForPage >= 0 && rawText) {
        targetPage = derivePageFromOffset(rawText, offsetForPage) ?? targetPage;
      }

      const docView = document.querySelector('[data-doc-render-view]');
      if (!docView) {
        if (targetPage) handleJumpToPage(targetPage);
        return;
      }

      // 清除上一次的高亮
      docView.querySelectorAll('.citation-highlight').forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (el.tagName === 'SPAN' && el.childNodes.length === 1 && el.firstChild?.nodeType === Node.TEXT_NODE) {
          const parent = el.parentNode;
          if (parent) {
            parent.replaceChild(document.createTextNode(el.textContent || ''), el);
            parent.normalize();
          }
        } else {
          htmlEl.classList.remove('citation-highlight');
          htmlEl.style.boxShadow = '';
          htmlEl.style.backgroundColor = '';
        }
      });
      docView.querySelectorAll('.row-highlight').forEach((el) => {
        const htmlEl = el as HTMLElement;
        htmlEl.classList.remove('row-highlight');
        htmlEl.style.boxShadow = '';
        htmlEl.style.backgroundColor = '';
      });
      docView.querySelectorAll('[data-media-filename]').forEach((element) => {
        (element as HTMLElement).style.removeProperty('box-shadow');
        (element as HTMLElement).style.removeProperty('transform');
      });

      if (citation.type === 'image' && citation.media_filename) {
        const targetSlideElement = targetPage
          ? docView.querySelector(`#doc-page-${targetPage}`)
          : null;
        const imageSearchRoot = targetSlideElement || docView;
        const imageElement = imageSearchRoot.querySelector(
          `[data-media-filename="${CSS.escape(citation.media_filename)}"]`,
        ) as HTMLElement | null;
        if (imageElement) {
          imageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          imageElement.style.transition = 'all 0.25s ease-in-out';
          imageElement.style.boxShadow = '0 0 0 4px rgba(0,113,227,0.55)';
          imageElement.style.transform = 'scale(1.015)';
          setTimeout(() => {
            imageElement.style.boxShadow = 'none';
            imageElement.style.transform = 'none';
          }, 2600);
          return;
        }
      }

      const found = findAndHighlightCitationContent(
        docView,
        citation,
        targetPage,
        isPaginatedPresentation,
      );
      if (!found && primaryAnchor) {
        // 页码不准时：不限页，全文再找一次具体段落。
        const foundGlobal = findAndHighlightCitationContent(
          docView,
          citation,
          undefined,
          isPaginatedPresentation,
        );
        if (foundGlobal) return;
      }
      if (!found && targetPage) {
        handleJumpToPage(targetPage);
      }
    }, 200);
  };

  /** 兼容后端可能返回的 ⟦N⟧ 和 [N]，渲染成与正文同基线的可点击引用。 */
  const renderInlineCitations = (
    kids: React.ReactNode,
    citations: DocQACitation[] | undefined,
  ): React.ReactNode =>
    React.Children.map(kids, (child) => {
      if (typeof child !== 'string' || (!child.includes('⟦') && !child.includes('['))) return child;
      return child.split(/((?:⟦\d+⟧|\[\d+\]))/g).map((part, i) => {
        const match = part.match(/(?:⟦(\d+)⟧|\[(\d+)\])/);
        if (!match) return part;
        const citationIndexText = match[1] || match[2];
        const citIdx = parseInt(citationIndexText, 10);
        const citation = citations?.find((c) => c.index === citIdx);
        if (!citation) return part;
        const displayLabel = getCitationDisplayLabel(citation, citIdx);
        const citationLocation = citation.page_number
          ? `第 ${citation.page_number} 页`
          : citation.row_key
            ? `数据行 ${citation.row_key}`
            : `引用 ${displayLabel}`;
        return (
          <button
            type="button"
            key={i}
            className="inline-citation cursor-pointer"
            title={`点击定位到${citationLocation}`}
            onClick={() => jumpToCitation(citation)}
          >
            [{displayLabel}]
          </button>
        );
      });
    });

  // 上传处理
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const realBlobUrl = URL.createObjectURL(file);


    const newDocId = `doc-${Date.now()}`;

    const newDoc: DocAssetItem = {
      id: newDocId,
      name: file.name,
      type: (() => {
        const ext = file.name.toLowerCase();
        if (ext.endsWith('.pdf')) return 'pdf';
        if (ext.endsWith('.xlsx') || ext.endsWith('.xls') || ext.endsWith('.csv')) return 'excel';
        if (ext.endsWith('.pptx') || ext.endsWith('.ppt') || ext.endsWith('.odp')) return 'ppt';
        if (ext.endsWith('.png') || ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.webp')) return 'image';
        if (ext.endsWith('.mp3') || ext.endsWith('.wav') || ext.endsWith('.m4a') || ext.endsWith('.aac')) return 'audio';
        return 'word';
      })(),
      size: `${(file.size / 1024).toFixed(1)} KB`,
      status: 'parsing',
      updatedAt: '解析中...',
      extractedText: '',
      fileUrl: realBlobUrl,
    };


    setDocs((prev) => [newDoc, ...prev]);
    setSelectedDocId(newDocId);
    showToast(`文档 《${file.name}》 已提交前置通用解析引擎`);

    try {
      const intentRes = await parseSourceIntent({ kind: 'file', file });
      setDocs((prev) =>
        prev.map((d) =>
          d.id === newDocId
            ? {
                ...d,
                status: 'ready',
                updatedAt: '刚刚',
                extractedText: intentRes.extractedTextExcerpt || '解析成功',
                intentResult: intentRes,
                docId: intentRes.docId || undefined,
                hasRawFile: Boolean(intentRes.docId),
              }
            : d
        )
      );
      showToast(`《${file.name}》 通用解析与资产沉淀完成！`);

      // 存储原始文件 Blob 供前端 docx-preview / pptx-viewer 渲染
      const officeExts = ['.pptx', '.ppt', '.docx', '.doc'];
      const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      if (officeExts.includes(fileExt)) {
        setDocs(prev => prev.map(d =>
          d.id === newDocId ? { ...d, fileBlob: file } : d
        ));
      }
    } catch (error: any) {
      console.error('File parsing failed:', error);
      showToast(`解析失败: ${error.message || '未知错误'}`);
      setDocs((prev) =>
        prev.map((d) =>
          d.id === newDocId
            ? {
                ...d,
                status: 'error',
                updatedAt: '刚刚',
                extractedText: `[解析失败] ${error.message || '无法连接到后端解析引擎或解析过程出错。'}`,
              }
            : d
        )
      );
    }
  };


  // 发送 Chat 问答
  const handleSendMessage = async (queryText: string) => {
    const q = queryText.trim();
    if (!q || chatLoading || !selectedDoc) return;

    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setChatLoading(true);

    try {
      const historyPayload = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await askDocumentQA(selectedDoc.extractedText, q, historyPayload, selectedDoc.docId);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.answer,
          citations: res.citations,
          usage: res.usage,
        },
      ]);
      if (res.suggested_followups) {
        setDynamicFollowups(res.suggested_followups);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `问答处理错误: ${(err as Error).message}`,
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // 触发表单提取
  const handleTriggerExtract = async (intent: DocIntent) => {
    if (!selectedDoc) return;
    setCurrentIntent(intent);
    setExtracting(true);
    try {
      const result = await parseSourceExtract({ kind: 'text', inputText: selectedDoc.extractedText }, intent);
      setExtractedPreview(result);
      showToast(`按【${intent}】模式完成表单字段提取`);
    } catch (err) {
      showToast(`抽取失败: ${(err as Error).message}`);
    } finally {
      setExtracting(false);
    }
  };

  // 推送到审核库
  const handlePushToReview = async () => {
    if (!extractedPreview || extractedPreview.candidates.length === 0) return;
    try {
      await commitParsedPreview(extractedPreview, extractedPreview.candidates[0]);
      showToast('已成功推送到竞品价格情报数据库！');
    } catch (err) {
      showToast(`推送失败: ${(err as Error).message}`);
    }
  };

  // 解析产物中的 media: 协议图片 → 本地库媒体文件真实 URL
  const resolveMediaSrc = (src?: string): string => {
    if (!src) return '';
    if (src.startsWith('media:') && selectedDoc?.docId) {
      return getDocHubMediaUrl(selectedDoc.docId, src.slice('media:'.length));
    }
    return src;
  };

  const startPanelResize = (
    panel: 'left' | 'right',
    mouseDownEvent: React.MouseEvent<HTMLDivElement>,
  ) => {
    mouseDownEvent.preventDefault();
    const initialPointerX = mouseDownEvent.clientX;
    const initialPanelWidth = panel === 'left' ? leftPanelWidth : rightPanelWidth;

    const handlePointerMove = (pointerEvent: MouseEvent) => {
      const workspaceWidth = workspaceRef.current?.getBoundingClientRect().width ?? window.innerWidth;
      const visibleOppositePanelWidth = panel === 'left'
        ? (rightPanelCollapsed ? 44 : rightPanelWidth)
        : (leftPanelCollapsed ? 44 : leftPanelWidth);
      const maximumPanelWidth = Math.max(
        panel === 'left' ? 320 : 420,
        workspaceWidth - visibleOppositePanelWidth - 460,
      );
      const pointerDelta = pointerEvent.clientX - initialPointerX;
      const requestedWidth = panel === 'left'
        ? initialPanelWidth + pointerDelta
        : initialPanelWidth - pointerDelta;
      const minimumPanelWidth = panel === 'left' ? 220 : 320;
      const absoluteMaximumWidth = panel === 'left' ? 440 : 720;
      const nextPanelWidth = Math.min(
        Math.max(requestedWidth, minimumPanelWidth),
        Math.min(maximumPanelWidth, absoluteMaximumWidth),
      );

      if (panel === 'left') {
        setLeftPanelWidth(nextPanelWidth);
      } else {
        setRightPanelWidth(nextPanelWidth);
      }
    };

    const stopPanelResize = () => {
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', stopPanelResize);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', stopPanelResize);
  };

  // 提取引擎状态徽章
  const renderEngineBadge = (pipeline: string[] = []) => {    if (pipeline.includes('anydoc')) {
      return (
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-600 flex items-center gap-1">
          <Zap size={10} /> AnyDoc 极速无损解析
        </span>
      );
    }
    if (pipeline.includes('mineru')) {
      return (
        <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-[10px] font-bold text-blue-600 flex items-center gap-1">
          <Database size={10} /> MinerU OCR 视觉解析
        </span>
      );
    }
    if (pipeline.includes('cloud')) {
      return (
        <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-[10px] font-bold text-orange-600 flex items-center gap-1">
          <Cloud size={10} /> 云端 OCR 降级兜底
        </span>
      );
    }
    if (pipeline.includes('pandas_excel') || pipeline.includes('csv')) {
      return (
        <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-[10px] font-bold text-purple-600 flex items-center gap-1">
          <TableIcon size={10} /> Pandas 精准表格解析
        </span>
      );
    }
    return null;
  };

  return (
    <div
      ref={workspaceRef}
      className="flex h-full w-full overflow-hidden bg-[var(--color-background-secondary)] text-[var(--color-system-primary)] font-sans"
    >
      {/* ======================= 1. 左栏：文档资产库 ======================= */}
      <div
        className="flex shrink-0 flex-col border-r border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] transition-[width] duration-200"
        style={{ width: leftPanelCollapsed ? 44 : leftPanelWidth }}
      >
        {leftPanelCollapsed ? (
          <button
            onClick={() => setLeftPanelCollapsed(false)}
            className="flex h-full w-full flex-col items-center gap-3 pt-4 text-[var(--color-system-secondary)] transition hover:bg-[var(--color-background-secondary)] hover:text-[var(--color-key)]"
            title="展开文档资产库"
          >
            <PanelLeftOpen size={17} />
            <Layers size={16} />
            <span className="text-[10px] font-semibold [writing-mode:vertical-rl]">文档资产库</span>
          </button>
        ) : (
          <>
        {/* Header */}
        <div className="flex h-[54px] items-center justify-between border-b border-[var(--color-system-quaternary)] px-4">
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-[var(--color-key)]" />
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--color-system-primary)]">
              文档资产库
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="rounded-full bg-[rgba(0,113,227,0.1)] px-2 py-0.5 text-[10px] font-bold text-[var(--color-key)]">
              {docs.length} 份
            </span>
            <button
              onClick={() => setLeftPanelCollapsed(true)}
              className="rounded-md p-1 text-[var(--color-system-tertiary)] transition hover:bg-[var(--color-background-secondary)] hover:text-[var(--color-key)]"
              title="折叠文档资产库"
            >
              <PanelLeftClose size={15} />
            </button>
          </div>
        </div>

        {/* 拖拽上传区域 */}
        <div className="p-3">
          <label className="group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4 transition hover:border-[var(--color-key)] hover:bg-[rgba(0,113,227,0.02)]">
            <input
              type="file"
              onChange={(e) => void handleFileUpload(e.target.files)}
              className="hidden"
              accept=".pdf,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.txt,.md,.markdown,.json,.png,.jpg,.jpeg,.webp,.odt,.ods,.odp,.rtf,.epub,.mp3,.wav,.m4a,.aac"
            />
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-background-primary)] shadow-sm group-hover:scale-105 transition">
              <FileUp size={18} className="text-[var(--color-key)]" />
            </div>
            <span className="mt-2 text-xs font-medium text-[var(--color-system-primary)]">
              上传并前置解析文档
            </span>
            <span className="text-[10px] text-[var(--color-system-secondary)]">
              支持 PDF / Excel / Word / PPT / 图片 / 音频
            </span>
          </label>
        </div>

        {/* 搜索过滤栏 */}
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] px-2.5 py-1.5 text-xs">
            <Search size={14} className="text-[var(--color-system-tertiary)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索文档名称..."
              className="w-full bg-transparent outline-none placeholder:text-[var(--color-system-tertiary)]"
            />
          </div>
        </div>

        {/* 文档资产列表 */}
        <div className="flex-1 overflow-y-auto px-2 space-y-1">
          {filteredDocs.map((doc) => {
            const isSelected = doc.id === selectedDocId;
            return (
              <div
                key={doc.id}
                onClick={() => setSelectedDocId(doc.id)}
                className={`group flex cursor-pointer flex-col rounded-xl p-3 text-xs transition ${
                  isSelected
                    ? 'bg-[rgba(0,113,227,0.08)] border border-[var(--color-key)]/30'
                    : 'hover:bg-[var(--color-background-secondary)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 truncate">
                    <FileText
                      size={16}
                      className={isSelected ? 'text-[var(--color-key)]' : 'text-[var(--color-system-secondary)]'}
                    />
                    <span className="truncate font-medium text-[var(--color-system-primary)]">
                      {doc.name}
                    </span>
                  </div>
                </div>

                <div className="mt-2 flex items-center justify-between text-[10px] text-[var(--color-system-secondary)]">
                  <span>{doc.size}</span>
                  <span className="flex items-center gap-1">
                    {doc.status === 'parsing' ? (
                      <>
                        <Loader2 size={10} className="animate-spin text-[var(--color-key)]" />
                        解析中
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={10} className="text-emerald-500" />
                        解析打底完成
                      </>
                    )}
                    {doc.docId && (
                      <button
                        onClick={(e) => void handleDeleteDoc(doc, e)}
                        className="ml-1 rounded p-0.5 text-[var(--color-system-tertiary)] opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-500"
                        title="从文档库删除"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
          </>
        )}
      </div>

      {!leftPanelCollapsed && (
        <div
          role="separator"
          aria-label="调整文档资产库宽度"
          onMouseDown={(event) => startPanelResize('left', event)}
          className="group relative z-20 w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-[rgba(0,113,227,0.12)]"
          title="拖动调整左栏宽度"
        >
          <GripVertical
            size={12}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[var(--color-system-tertiary)] opacity-0 transition group-hover:opacity-100"
          />
        </div>
      )}

      {/* ======================= 2. 中栏：文档解析浏览 ======================= */}
      <div className="flex min-w-0 flex-1 flex-col border-r border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)]">
        {/* Header */}
        <div className="flex h-[54px] items-center justify-between border-b border-[var(--color-system-quaternary)] px-6">
          <div className="flex items-center gap-2 truncate">
            <BookOpen size={18} className="text-[var(--color-key)]" />
            <h2 className="truncate text-xs font-semibold text-[var(--color-system-primary)]">
              {selectedDoc ? selectedDoc.name : '请选择文档'}
            </h2>
            {selectedDoc && renderEngineBadge(selectedDoc.intentResult?.pipeline)}
            {selectedDoc?.intentResult && (
              <span
                className="rounded-full bg-[rgba(52,199,89,0.15)] px-2.5 py-0.5 text-[10px] font-bold text-emerald-600"
                title={selectedDoc.intentResult.intent.reason}
              >
                {{ price_quote: '报价单', spec_sheet: '规格书', tender_checklist: '招标应答', research_note: '调研纪要', general: '通用文档' }[selectedDoc.intentResult.intent.intent] || '通用文档'}
                {' '}({Math.round(selectedDoc.intentResult.intent.confidence * 100)}%)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-lg bg-[var(--color-background-secondary)] p-1 border border-[var(--color-system-quaternary)]">
            <button
              onClick={() => setViewMode('rendered')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                viewMode === 'rendered'
                  ? 'bg-[var(--color-background-primary)] text-[var(--color-key)] shadow-sm font-semibold'
                  : 'text-[var(--color-system-secondary)] hover:text-[var(--color-system-primary)]'
              }`}
            >
              <Eye size={13} />
              {selectedDoc?.type === 'ppt' ? '幻灯片阅读' : '渲染富文本'}
            </button>
            <button
              onClick={() => setViewMode('original')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                viewMode === 'original'
                  ? 'bg-[var(--color-background-primary)] text-[var(--color-key)] shadow-sm font-semibold'
                  : 'text-[var(--color-system-secondary)] hover:text-[var(--color-system-primary)]'
              }`}
            >
              <FileText size={13} />
              原始文件视图
            </button>
            <button
              onClick={() => setViewMode('source')}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                viewMode === 'source'
                  ? 'bg-[var(--color-background-primary)] text-[var(--color-key)] shadow-sm font-semibold'
                  : 'text-[var(--color-system-secondary)] hover:text-[var(--color-system-primary)]'
              }`}
            >
              <Code size={13} />
              Markdown 源码
            </button>
            </div>
          </div>

        </div>

        {selectedDoc ? (
          <DocHubDocumentContent
            selectedDoc={selectedDoc}
            viewMode={viewMode}
            docSummary={docSummary}
            onViewModeChange={setViewMode}
            onImagePreview={setImagePreview}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-[var(--color-system-secondary)]">
            请在左侧选择或上传一份文档
          </div>
        )}

      </div>

      {/* ======================= 3. 右栏：AI 智能中枢 (Chat & Multi-Task) ======================= */}
      {!rightPanelCollapsed && (
        <div
          role="separator"
          aria-label="调整智能问答栏宽度"
          onMouseDown={(event) => startPanelResize('right', event)}
          className="group relative z-20 w-1 shrink-0 cursor-col-resize bg-transparent hover:bg-[rgba(0,113,227,0.12)]"
          title="拖动调整右栏宽度"
        >
          <GripVertical
            size={12}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[var(--color-system-tertiary)] opacity-0 transition group-hover:opacity-100"
          />
        </div>
      )}
      <div
        className="flex shrink-0 flex-col bg-[var(--color-background-primary)] transition-[width] duration-200"
        style={{ width: rightPanelCollapsed ? 44 : rightPanelWidth }}
      >
        {rightPanelCollapsed ? (
          <button
            onClick={() => setRightPanelCollapsed(false)}
            className="flex h-full w-full flex-col items-center gap-3 pt-4 text-[var(--color-system-secondary)] transition hover:bg-[var(--color-background-secondary)] hover:text-[var(--color-key)]"
            title="展开智能问答"
          >
            <PanelRightOpen size={17} />
            <MessageSquareText size={16} />
            <span className="text-[10px] font-semibold [writing-mode:vertical-rl]">智能问答</span>
          </button>
        ) : (
          <>
        {/* Header Tabs */}
        <div className="flex h-[54px] items-center border-b border-[var(--color-system-quaternary)] px-3">
          <button
            onClick={() => setActiveRightTab('chat')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition ${
              activeRightTab === 'chat'
                ? 'bg-[rgba(0,113,227,0.1)] text-[var(--color-key)]'
                : 'text-[var(--color-system-secondary)] hover:bg-[var(--color-background-secondary)]'
            }`}
          >
            <MessageSquareText size={14} />
            💬 智能问答
          </button>
          <button
            onClick={() => setActiveRightTab('extract')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition ${
              activeRightTab === 'extract'
                ? 'bg-[rgba(0,113,227,0.1)] text-[var(--color-key)]'
                : 'text-[var(--color-system-secondary)] hover:bg-[var(--color-background-secondary)]'
            }`}
          >
            <FileCheck size={14} />
            📋 动态抽取
          </button>
          <button
            onClick={() => setActiveRightTab('kb')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition ${
              activeRightTab === 'kb'
                ? 'bg-[rgba(0,113,227,0.1)] text-[var(--color-key)]'
                : 'text-[var(--color-system-secondary)] hover:bg-[var(--color-background-secondary)]'
            }`}
          >
            <Database size={14} />
            📚 知识库
          </button>
          <button
            onClick={() => setRightPanelCollapsed(true)}
            className="ml-1 shrink-0 rounded-md p-1.5 text-[var(--color-system-tertiary)] transition hover:bg-[var(--color-background-secondary)] hover:text-[var(--color-key)]"
            title="折叠智能问答栏"
          >
            <PanelRightClose size={15} />
          </button>
        </div>

        {/* Tab 1: 智能问答 (Doc Chat) */}
        {activeRightTab === 'chat' && (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* 对话历史 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-[96%] rounded-2xl px-4 py-3 text-xs leading-relaxed shadow-xs ${
                      msg.role === 'user'
                        ? 'bg-[var(--color-key)] text-white'
                        : 'border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] text-[var(--color-system-primary)]'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <div className="whitespace-pre-wrap font-medium">{msg.content}</div>
                    ) : (
                      <div className="prose prose-xs max-w-none text-xs dark:prose-invert">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkMath]}
                          rehypePlugins={[[rehypeKatex, { strict: false, throwOnError: false }]]}
                          urlTransform={allowMediaUrlTransform}
                          components={{
                            p({ children }) {
                              return (
                                <p className="my-1 font-sans text-xs leading-relaxed text-[var(--color-system-primary)]">
                                  {renderInlineCitations(children, msg.citations)}
                                </p>
                              );
                            },
                            strong({ children }) {
                              return (
                                <strong className="font-bold text-[var(--color-key)] bg-[rgba(0,113,227,0.08)] px-1 py-0.5 rounded border border-[var(--color-key)]/20">
                                  {children}
                                </strong>
                              );
                            },
                            ul({ children }) {
                              return <ul className="my-1.5 list-disc space-y-1 pl-4 text-xs marker:text-[9px] marker:text-[var(--color-key)]">{children}</ul>;
                            },
                            ol({ children }) {
                              return (
                                <ol className="my-1.5 list-decimal space-y-1.5 pl-[18px] text-xs marker:text-[9px] marker:font-semibold marker:text-[var(--color-key)]">
                                  {children}
                                </ol>
                              );
                            },
                            li({ children }) {
                              return (
                                <li className="text-xs text-[var(--color-system-primary)]">
                                  {renderInlineCitations(children, msg.citations)}
                                </li>
                              );
                            },
                            table({ children }) {
                              return (
                                <div className="my-2 overflow-x-auto rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)]">
                                  <table className="w-full border-collapse text-[11px] text-left">{children}</table>
                                </div>
                              );
                            },
                            th({ children }) {
                              return <th className="px-2 py-1 bg-[var(--color-background-secondary)] font-semibold border-b border-[var(--color-system-quaternary)]">{children}</th>;
                            },
                            td({ children }) {
                              return <td className="px-2 py-1 border-t border-[var(--color-system-quaternary)]">{children}</td>;
                            },
                            code({ children }) {
                              return (
                                <code className="rounded bg-[var(--color-background-primary)] px-1 py-0.5 font-mono text-[10px] text-[var(--color-key)] border border-[var(--color-system-quaternary)]">
                                  {children}
                                </code>
                              );
                            },
                            img({ src, alt }) {
                              const rawSrc = typeof src === 'string' ? src : undefined;
                              const mediaFilename = rawSrc?.startsWith('media:') ? rawSrc.slice('media:'.length) : undefined;
                              const imageCitation = msg.citations?.find(
                                (citation) => citation.media_filename === mediaFilename,
                              );
                              return (
                                <span className="my-2.5 block overflow-hidden rounded-xl border border-blue-100 bg-white shadow-xs not-prose">
                                  <button
                                    type="button"
                                    onClick={() => imageCitation && jumpToCitation(imageCitation)}
                                    className="group block w-full text-left"
                                    title={imageCitation ? '点击定位到原幻灯片图片' : (alt || '')}
                                  >
                                    <span className="flex max-h-64 min-h-32 items-center justify-center overflow-hidden bg-slate-50">
                                      <img
                                        src={resolveMediaSrc(rawSrc)}
                                        alt={alt || '文档配图'}
                                        data-media-filename={mediaFilename}
                                        className="max-h-64 w-full object-contain transition duration-200 group-hover:scale-[1.01]"
                                      />
                                    </span>
                                    <span className="flex items-start justify-between gap-2 border-t border-blue-50 px-2.5 py-2 text-[9px] font-bold text-[var(--color-key)]">
                                      <span>{imageCitation?.caption || alt || '幻灯片图示'}</span>
                                      {imageCitation?.page_number && (
                                        <span className="shrink-0">第 {imageCitation.page_number} 页</span>
                                      )}
                                    </span>
                                  </button>
                                </span>
                              );
                            },
                          }}
                        >
                          {prepareAssistantMarkdown(msg.content, msg.citations)}
                        </ReactMarkdown>
                      </div>
                    )}

                    {msg.citations?.some((citation) => citation.type !== 'image') && (
                      <div className="mt-3 border-t border-dashed border-[var(--color-system-quaternary)] pt-2">
                        <div className="text-[10px] font-bold text-[var(--color-key)] flex items-center gap-1 mb-1.5">
                          <BookOpen size={11} />
                          引用来源
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {msg.citations
                            .filter((citation) => citation.type !== 'image')
                            .map((citation: DocQACitation, citationIndex: number) => {
                            const displayLabel = getCitationDisplayLabel(
                              citation,
                              citationIndex + 1,
                            );
                            return (
                              <button
                                key={citationIndex}
                                onClick={() => jumpToCitation(citation)}
                                className="group inline-flex items-center gap-1 rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] px-2 py-1 text-[10px] transition-all hover:border-[var(--color-key)] hover:shadow-sm"
                                title={citation.row_key ? `定位到行: ${citation.row_key}` : citation.excerpt}
                              >
                                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-key)] text-[9px] font-bold text-white">
                                  {displayLabel}
                                </span>
                                <span className="max-w-[120px] truncate text-[var(--color-system-secondary)]">
                                  {citation.row_key ? `行: ${citation.row_key}` : citation.page_number ? `第${citation.page_number}页` : '数据来源'}
                                </span>
                                {citation.relevance_score != null && citation.relevance_score > 0 && (
                                  <span className="text-[9px] font-semibold text-emerald-600">
                                    {(citation.relevance_score * 100).toFixed(0)}%
                                  </span>
                                )}
                                <ArrowUpRight size={10} className="text-[var(--color-key)] opacity-0 transition-opacity group-hover:opacity-100" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {msg.usage && (
                      <div
                        className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-[var(--color-system-tertiary)]"
                        title={`问答模式: ${msg.usage.mode} | LLM 调用 ${msg.usage.llm_calls} 次 | token 来源: ${msg.usage.token_source === 'api' ? 'API 返回' : '按字符估算'}`}
                      >
                        <span className="font-semibold">{msg.usage.total_seconds}s</span>
                        {msg.usage.llm_seconds > 0 && (
                          <span>(LLM {msg.usage.llm_seconds}s)</span>
                        )}
                        <span>·</span>
                        <span>
                          输入 {msg.usage.prompt_tokens >= 1000 ? `${(msg.usage.prompt_tokens / 1000).toFixed(1)}K` : msg.usage.prompt_tokens} tok
                        </span>
                        <span>·</span>
                        <span>
                          输出 {msg.usage.completion_tokens >= 1000 ? `${(msg.usage.completion_tokens / 1000).toFixed(1)}K` : msg.usage.completion_tokens} tok
                        </span>
                        {msg.usage.token_source === 'estimate' && (
                          <span className="opacity-70">(估算)</span>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              ))}

              {chatLoading && (
                <div className="flex items-center gap-2 text-xs text-[var(--color-system-secondary)]">
                  <Loader2 size={14} className="animate-spin text-[var(--color-key)]" />
                  <span>AI 正在检索 Chunk 并解答...</span>
                </div>
              )}
            </div>

            {/* 智能追问 Chips：随每轮问答动态更新，问过即移除，可折叠 */}
            <div className="border-t border-[var(--color-system-quaternary)] px-3 py-2 bg-[var(--color-background-secondary)]/50">
              <button
                type="button"
                onClick={() => setFollowupPanelOverride(!followupsExpanded)}
                className="flex w-full items-center justify-between text-[10px] font-semibold text-[var(--color-key)] transition hover:opacity-80"
                title={followupsExpanded ? '收起提问推荐' : '展开提问推荐'}
              >
                <span className="flex items-center gap-1">
                  <Sparkles size={11} />
                  专属提问推荐
                  {followupChips.length > 0 && (
                    <span className="rounded-full bg-[rgba(0,113,227,0.1)] px-1.5 py-px text-[9px] font-bold">
                      {followupChips.length}
                    </span>
                  )}
                </span>
                {followupsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              {followupsExpanded && (
                followupChips.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {followupChips.map((chip) => (
                      <button
                        key={chip}
                        onClick={() => void handleSendMessage(chip)}
                        className="rounded-lg border border-[rgba(0,113,227,0.2)] bg-[var(--color-background-primary)] px-2.5 py-1 text-[10px] font-medium text-[var(--color-system-primary)] hover:border-[var(--color-key)] hover:text-[var(--color-key)] shadow-xs transition"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1.5 text-[10px] leading-relaxed text-[var(--color-system-tertiary)]">
                    推荐问题都问过了，直接在下方输入新问题继续追问即可。
                  </p>
                )
              )}
            </div>


            <DocHubChatComposer loading={chatLoading} onSend={handleSendMessage} />
          </div>
        )}

        {/* Tab 2: 动态表单抽取 (Extract) */}
        {activeRightTab === 'extract' && (
          <div className="flex flex-1 flex-col overflow-y-auto p-4 space-y-4">
            <div className="rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4">
              <label className="text-xs font-semibold text-[var(--color-system-primary)]">
                选择抽取意图 / Schema 范式
              </label>
              <select
                value={currentIntent}
                onChange={(e) => void handleTriggerExtract(e.target.value as DocIntent)}
                className="mt-2 w-full rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] px-3 py-2 text-xs outline-none"
              >
                <option value="price_quote">竞品报价表 (Price Quote Schema)</option>
                <option value="spec_sheet">产品规格书 (Spec Sheet Schema)</option>
                <option value="tender_checklist">招标应答表 (Tender Checklist)</option>
                <option value="research_note">调研纪要 (Research Note)</option>
              </select>
              <button
                onClick={() => void handleTriggerExtract(currentIntent)}
                disabled={extracting}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[var(--color-key)] py-2 text-xs font-medium text-white"
              >
                {extracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                执行【{currentIntent}】表单提取
              </button>
            </div>

            {/* 提取成果展示 */}
            {extractedPreview && (
              <div className="rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[var(--color-system-primary)]">
                    抽取字段预览 ({extractedPreview.candidates.length} 条)
                  </span>
                  <span className="text-[10px] text-emerald-600">模型：{extractedPreview.model || 'gpt-5.6-luna'}</span>
                </div>

                {extractedPreview.candidates.map((cand, idx) => (
                  <div key={idx} className="rounded-lg border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-3 text-xs space-y-1.5">
                    <div className="flex justify-between font-bold text-[var(--color-system-primary)]">
                      <span>{cand.competitor || '竞品品牌'} - {cand.model || '型号'}</span>
                      <span className="text-[var(--color-key)]">{cand.price_per_sqm ? `USD ${cand.price_per_sqm}/m²` : '无价格'}</span>
                    </div>
                    <div className="text-[10px] text-[var(--color-system-secondary)]">
                      点间距: {cand.pitch_mm ? `${cand.pitch_mm} mm` : '-'} | 地区: {cand.region || '-'}
                    </div>
                  </div>
                ))}

                <button
                  onClick={() => void handlePushToReview()}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2 text-xs font-medium text-white transition hover:bg-emerald-700"
                >
                  <ArrowUpRight size={14} />
                  一键推送至竞品情报审核库
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: 知识库面板 (Knowledge Base) */}
        {activeRightTab === 'kb' && (
          <div className="flex flex-1 flex-col overflow-y-auto p-4 space-y-4">
            {/* 问答路径分级 */}
            {qaTierInfo && (
              <div className="rounded-2xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4 text-xs space-y-2">
                <div className="flex items-center gap-2 font-semibold text-[var(--color-system-primary)]">
                  <Zap size={14} className="text-[var(--color-key)]" />
                  问答处理路径
                </div>
                <div className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-bold ${qaTierInfo.color}`}>
                  {qaTierInfo.tier}
                </div>
                <p className="text-[11px] leading-relaxed text-[var(--color-system-secondary)]">
                  {qaTierInfo.description}
                </p>
              </div>
            )}

            {/* 索引与切片 */}
            <div className="rounded-2xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4 text-xs space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-semibold text-[var(--color-system-primary)]">
                  <Database size={16} className="text-[var(--color-key)]" />
                  向量索引切片
                </div>
                {selectedDoc?.docId && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void loadKbChunks(selectedDoc.docId!)}
                      disabled={kbChunksLoading}
                      className="text-[10px] text-[var(--color-system-secondary)] hover:text-[var(--color-key)] hover:underline disabled:opacity-50"
                    >
                      {kbChunksLoading ? '加载中…' : '刷新'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleReindexDoc()}
                      disabled={reindexing}
                      title={indexStatus?.can_resume
                        ? `检测到未完成索引，将从 ${indexStatus.checkpoint_embedded_count}/${indexStatus.checkpoint_embed_total || indexStatus.index_embed_total || '?'} 继续`
                        : qaTierInfo?.key === 'direct'
                          ? '短文档以全文直送为主；仍可重建检索结构/清理旧向量'
                          : qaTierInfo?.key === 'on_demand'
                            ? '重建章节 span 向量索引，供语义召回 + Rerank'
                            : '用最新解析内容重建检索索引'}
                      className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-key)]/30 bg-[rgba(0,113,227,0.08)] px-2 py-1 text-[10px] font-semibold text-[var(--color-key)] transition hover:bg-[rgba(0,113,227,0.14)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reindexing ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
                      {reindexing
                        ? '重建中…'
                        : indexStatus?.can_resume
                          && (indexStatus.checkpoint_embedded_count || 0) > 0
                          ? `继续重建 (${indexStatus.checkpoint_embedded_count}/${indexStatus.checkpoint_embed_total || indexStatus.index_embed_total || '?'})`
                          : (qaTierInfo?.reindexLabel || '重建检索索引')}
                    </button>
                  </div>
                )}
              </div>

              {kbChunksLoading && (
                <div className="flex items-center gap-2 text-[11px] text-[var(--color-system-secondary)]">
                  <Loader2 size={14} className="animate-spin" />
                  正在读取向量库切片…
                </div>
              )}
              {kbChunksError && (
                <div className="text-[11px] text-red-500">读取失败: {kbChunksError}</div>
              )}

              {kbChunks && !kbChunksLoading && (
                <>
                  <div className="rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] p-3 text-[11px] space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">索引状态:</span>
                      <span className={`font-bold ${
                        kbChunks.index_status === 'ready' ? 'text-emerald-600'
                          : kbChunks.index_status === 'failed' ? 'text-red-500'
                            : 'text-amber-600'
                      }`}>
                        {{
                          not_started: '尚未建立',
                          queued: '等待重建',
                          running: '正在建立',
                          ready: '可查询',
                          failed: '建立失败',
                          cancelled: '已取消',
                        }[kbChunks.index_status]}
                      </span>
                    </div>
                    {indexTrackingDocId === selectedDoc?.docId && indexStatus && (indexStatus.index_status === 'queued' || indexStatus.index_status === 'running') && (
                      <div className="space-y-1.5 pt-1">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-[var(--color-system-secondary)]">重建进度</span>
                          <span className="font-semibold text-[var(--color-key)]">{Math.round(indexStatus.index_progress * 100)}%</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-background-secondary)]">
                          <div
                            className="h-full rounded-full bg-[var(--color-key)] transition-all duration-500"
                            style={{ width: `${Math.round(indexStatus.index_progress * 100)}%` }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setIndexModalVisible(true)}
                          className="text-[10px] font-medium text-[var(--color-key)] hover:underline"
                        >
                          查看进度 / 终止重建
                        </button>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">检索策略:</span>
                      <span className="font-semibold text-[var(--color-system-primary)]">{qaTierInfo?.tier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">源文档:</span>
                      <span>{(kbChunks.text_length / 1000).toFixed(1)}K 字符</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">可检索覆盖:</span>
                      <span>{(kbChunks.indexed_text_length / 1000).toFixed(1)}K 字符（{Math.round(kbChunks.index_coverage_ratio * 100)}%）</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">原文检索块:</span>
                      <span className="font-bold text-[var(--color-system-primary)]">{kbChunks.source_span_count} 个</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">向量切片:</span>
                      <span>{kbChunks.vector_chunk_count} 个</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">含图片理解:</span>
                      <span className={kbChunks.has_media_enriched_text ? 'font-semibold text-emerald-600' : 'text-[var(--color-system-tertiary)]'}>
                        {kbChunks.has_media_enriched_text ? '是（图转文已进入索引）' : '否'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">生成式摘要节点:</span>
                      <span>{kbChunks.summary_node_count} 个（默认不启用 RAPTOR）</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-system-secondary)]">当前展示:</span>
                      <span>{kbChunks.returned_chunk_count} / {kbChunks.chunk_count}</span>
                    </div>
                  </div>

                  {kbChunks.index_error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-[11px] text-red-600">
                      最近错误：{kbChunks.index_error}
                    </div>
                  )}

                  {kbChunks.chunk_count === 0 ? (
                    <div className="rounded-xl border border-dashed border-[var(--color-system-quaternary)] p-3 text-[11px] text-[var(--color-system-tertiary)]">
                      {qaTierInfo?.key === 'direct'
                        ? '短文档使用全文直送，不依赖检索切片；如需清理旧向量可点击上方「重建检索结构」。'
                        : qaTierInfo?.key === 'on_demand'
                          ? '该超大文档尚未建立原文检索地图，请点击上方「重建 span 向量索引」。'
                          : '该文档尚未建立混合检索索引，请点击上方「重建混合检索索引」。'}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                      {kbChunks.chunks.map((chunk) => (
                        <div
                          key={chunk.seq}
                          className="rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] p-2.5 text-[10px] space-y-1"
                        >
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-[var(--color-system-primary)]">#{chunk.seq}</span>
                            {chunk.chunk_kind === 'source_span' && (
                              <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-blue-600 font-semibold">原文检索块</span>
                            )}
                            {chunk.is_summary_node && (
                              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-600 font-semibold">摘要 L{chunk.raptor_level}</span>
                            )}
                            {chunk.has_image_insight && (
                              <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-600 font-semibold">含图片理解</span>
                            )}
                            {chunk.page_number && (
                              <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-blue-600">第 {chunk.page_number} 页</span>
                            )}
                            <span className="ml-auto text-[var(--color-system-tertiary)]">{chunk.char_count} 字符</span>
                          </div>
                          {chunk.section_path && (
                            <div className="truncate text-[var(--color-key)]">{chunk.section_path}</div>
                          )}
                          <p className="leading-relaxed text-[var(--color-system-secondary)] line-clamp-3">
                            {chunk.preview}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedDoc?.docId && <DocHubRecallTestPanel docId={selectedDoc.docId} />}
          </div>
        )}
          </>
        )}
      </div>

      {/* ======================= 图片放大预览灯箱 ======================= */}
      <DocHubIndexProgressModal
        open={indexModalVisible && Boolean(indexStatus)}
        documentName={indexTrackingDoc?.name || selectedDoc?.name || '当前文档'}
        status={indexStatus}
        cancelling={indexCancelling}
        onCancel={() => void handleCancelIndexRebuild()}
        onClose={handleCloseIndexModal}
      />

      {imagePreview && (
        <div
          role="dialog"
          aria-label="图片放大预览"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-6 backdrop-blur-sm"
          onClick={() => setImagePreview(null)}
        >
          <button
            type="button"
            aria-label="关闭图片预览"
            className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/25"
            onClick={() => setImagePreview(null)}
          >
            <X size={18} />
          </button>
          <img
            src={imagePreview.src}
            alt={imagePreview.alt}
            className="max-h-full max-w-full cursor-zoom-out rounded-lg object-contain shadow-2xl"
            onClick={() => setImagePreview(null)}
          />
          {imagePreview.alt && (
            <div className="pointer-events-none absolute bottom-5 left-1/2 max-w-[80vw] -translate-x-1/2 truncate rounded-full bg-slate-950/60 px-4 py-1.5 text-[11px] text-slate-200">
              {imagePreview.alt}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
